import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LANGUAGE_OPTIONS } from '../../content/translations';
import { ASSETS, EXTERNAL_LINKS, PRIMARY_NAV } from '../../config/site';
import { useLocale } from '../../context/LocaleContext';
import { ArrowIcon } from '../ui/Icons';

export default function SiteHeader() {
  const { locale, t, changeLocale, isChanging } = useLocale();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const languageRef = useRef(null);
  const languageTriggerRef = useRef(null);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setMenuOpen(false);
      setLanguageOpen(false);
      setHeaderHidden(false);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!languageRef.current?.contains(event.target)) setLanguageOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        const returnFocus = languageRef.current?.contains(document.activeElement);
        setMenuOpen(false);
        setLanguageOpen(false);
        if (returnFocus) window.requestAnimationFrame(() => languageTriggerRef.current?.focus());
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    let lastY = window.scrollY;
    let direction = 0;
    let distance = 0;
    let frame = 0;

    const updateHeader = () => {
      const nextY = Math.max(0, window.scrollY);
      const delta = nextY - lastY;
      lastY = nextY;
      frame = 0;

      const focusedElement = document.activeElement;
      const headerHasKeyboardFocus = focusedElement?.closest('.site-header') && focusedElement.matches(':focus-visible');
      if (nextY < 96 || menuOpen || languageOpen || headerHasKeyboardFocus) {
        direction = 0;
        distance = 0;
        setHeaderHidden(false);
        return;
      }

      if (Math.abs(delta) < 1) return;
      const nextDirection = delta > 0 ? 1 : -1;
      if (nextDirection !== direction) {
        direction = nextDirection;
        distance = 0;
      }
      distance += Math.abs(delta);

      const threshold = nextDirection > 0 ? 24 : 8;
      if (distance < threshold) return;
      setHeaderHidden(nextDirection > 0);
      distance = 0;
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateHeader);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.cancelAnimationFrame(frame);
    };
  }, [languageOpen, menuOpen]);

  return (
      <header className="site-header" data-hidden={headerHidden || undefined} onFocusCapture={() => setHeaderHidden(false)}>
        <Link className="site-header__brand" to="/" aria-label={t.common.aria.home}>
          <img src={ASSETS.logo} alt="YourApplicant" />
        </Link>

        <nav id="primary-navigation" className="primary-nav" aria-label={t.common.aria.mainNavigation} data-open={menuOpen || undefined}>
          {PRIMARY_NAV.map(([key, to]) => (
            <NavLink key={key} to={to} end={to === '/'} className={({ isActive }) => (isActive ? 'is-active' : undefined)}>
              {t.common.nav[key]}
            </NavLink>
          ))}
        </nav>

        <div className="site-header__actions">
          <div className="language-picker" ref={languageRef} data-open={languageOpen || undefined}>
            <button
              className="language-picker__trigger"
              ref={languageTriggerRef}
              type="button"
              aria-expanded={languageOpen}
              aria-label={t.common.aria.selectLanguage}
              onClick={() => setLanguageOpen((open) => !open)}
            >
              <span>{locale.toUpperCase()}</span>
              <i aria-hidden="true" />
            </button>
            <div className="language-picker__menu" aria-label={t.common.aria.languageMenu}>
              {LANGUAGE_OPTIONS.map((language) => (
                <button
                  type="button"
                  data-active={language.code === locale || undefined}
                  key={language.code}
                  disabled={isChanging}
                  onClick={() => {
                    setLanguageOpen(false);
                    changeLocale(language.code);
                  }}
                >
                  <span>{language.shortLabel ?? language.code.toUpperCase()}</span>
                  <strong>{language.label}</strong>
                  <i aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>

          <a className="header-login" href={EXTERNAL_LINKS.app}>
            <span>{t.common.actions.login}</span><ArrowIcon />
          </a>

          <button
            type="button"
            className="menu-toggle"
            aria-label={menuOpen ? t.common.aria.closeNavigation : t.common.aria.openNavigation}
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span /><span />
          </button>

        </div>
      </header>
  );
}
